// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { isCommunicationUserIdentifier } from '@azure/communication-common';
import { CallWithChatAdapter } from '@azure/communication-react';
import { utils } from './Utils';

const TIME_TO_PRUNE = 2 * 60 * 1000; // 2 minutes

/**
 * Legacy code to prune chat users to avoid hitting the 250 participant limit.
 *
 * As participants always join as a new user, recurring meetings will hit this limit very quickly.
 * TODO: Instead of pruning old users, have new users join with the same identity to avoid hitting the 250 limit.
 */
export const pruneUsers = async (args: {
  adapter: CallWithChatAdapter;
  functionsAppToken: string;
  threadId: string;
}): Promise<boolean> => {
  const { adapter, functionsAppToken, threadId } = args;

  const callWithChatState = adapter?.getState();

  if (!callWithChatState || !callWithChatState.userId || !isCommunicationUserIdentifier(callWithChatState.userId)) {
    // if the user is not an acs user then don't bother trying to prune.
    return true;
  }
  const currentUser = callWithChatState.userId;
  const chatParticipants = callWithChatState.chat?.participants;
  const userIds: string[] = [];

  // Wait for 2 min to make sure users have enough time to join the call, users join the chat upon page load, and join the call when join button clicked.
  // If we do not wait, a user may be kicked out the moment they open the page but before they join the call.
  await utils.delay(TIME_TO_PRUNE);
  const callParticipants = callWithChatState.call?.remoteParticipants;

  if (callParticipants && chatParticipants) {
    for (const userId in chatParticipants) {
      if (!callParticipants[userId]) {
        const chatParticipant = chatParticipants[userId];
        if (
          chatParticipant.displayName &&
          chatParticipant.displayName !== 'admin' &&
          userId !== currentUser.communicationUserId
        ) {
          userIds.push(userId);
        }
      }
    }
  }

  if (functionsAppToken && userIds?.length > 0 && threadId) {
    const userRemoveResponse = await utils.removeUsersFromChatThread(functionsAppToken, threadId, userIds);
    if (!userRemoveResponse.ok) {
      console.error(`User pruning failed :${userRemoveResponse.status} ${userRemoveResponse.statusText}`);
      return false;
    }
  }

  return true;
};
